import {Box} from '@viptech/react-components'
import Hls from 'hls.js'
import React, {useEffect, useRef, useState} from 'react'
import Loading from 'src/components/Loading'
import useInterestArea from 'src/hooks/api/interest-areas/useInterestAreas'
import storage from 'src/services/storage'

interface Props {
  src: string
  thumbnail?: string
  retryDelayMs?: number
  height?: string
  cameraId?: number
  drawBarrierCoordinates: boolean
  playbackSpeeedRate?: number
  loop?: boolean
  showControls?: boolean
  setIsErrorOnStream?: (isError: boolean) => void
}

const VideoPlayer: React.FC<Props> = ({
  src,
  thumbnail = '',
  retryDelayMs = 3000,
  height = '345px',
  cameraId,
  drawBarrierCoordinates,
  playbackSpeeedRate = 1,
  loop = false,
  showControls = false,
  setIsErrorOnStream,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isError, setIsError] = useState(false)

  const [size, setSize] = useState({width: 0, height: 0})

  useEffect(() => {
    if (!videoRef.current) return
    const element = videoRef.current
    const resize = new ResizeObserver(([entry]) => {
      const cr = entry.contentRect
      setSize({width: Math.round(cr.width), height: Math.round(cr.height)})
    })
    resize.observe(element)
    return () => resize.disconnect()
  }, [])

  const scheduleRetry = () => {
    const exponentialDelay = retryDelayMs * Math.min(8, 2 ** retryCount)
    const jitterMs = Math.floor(Math.random() * 700)
    setTimeout(() => {
      setRetryCount((prev) => prev + 1)
    }, exponentialDelay + jitterMs)
  }

  const setupHLS = () => {
    const video = videoRef.current
    if (!video || !Hls.isSupported()) return

    const hls = new Hls({
      startLevel: 0,
      autoStartLoad: true,
      lowLatencyMode: true,
      maxBufferLength: 12,
      maxMaxBufferLength: 20,
      maxBufferSize: 18 * 1000 * 1000,
      backBufferLength: 8,
      liveSyncDurationCount: 2,
      liveMaxLatencyDurationCount: 6,
      xhrSetup: (xhr) => {
        xhr.setRequestHeader('Authorization', `Bearer ${storage.get('token')}`)
      },
    })
    hlsRef.current = hls

    hls.loadSource(src)
    hls.attachMedia(video)

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.OTHER_ERROR:
          case Hls.ErrorTypes.NETWORK_ERROR:
            setIsPlaying(false)
            scheduleRetry()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            setIsPlaying(false)
            hls.recoverMediaError()
            scheduleRetry()
            break
          default:
            setIsError(true)
            setIsErrorOnStream?.(true)
            hls.destroy()
            break
        }
      }
    })
  }

  useEffect(() => {
    const video = videoRef.current
    setIsPlaying(false)
    setIsError(false)
    setIsErrorOnStream?.(false)
    if (Hls.isSupported()) setupHLS()
    else if (video && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src
      video.load()
    }

    return () => {
      hlsRef.current?.destroy()
    }
  }, [src, retryCount])

  const {data: barriers} = useInterestArea({
    cameraId: cameraId ?? 0,
    enabled: !!cameraId,
  })

  const colorsOnSvg = [
    'blue',
    'red',
    '#468FAF',
    '#6D597A',
    '#B56576',
    '#4F759B',
    '#E76F51',
    '#2A9D8F',
    '#F4A261',
    '#8AB17D',
    '#D4A373',
    '#3D5A80',
    '#EE6C4D',
    '#A7C957',
    '#F28585',
    '#8E9AAF',
    '#DDA15E',
    '#94D2BD',
    '#E9C46A',
    '#5E6472',
    '#B5838D',
    '#FFB703',
    '#AC3B61',
    '#5A189A',
    '#E63946',
    '#9B5DE5',
    '#F28482',
    '#457B9D',
    '#E5989B',
  ]

  const setPlaybackSpeed = (rate: number) => {
    if (!videoRef.current) return
    videoRef.current.playbackRate = rate
  }

  useEffect(() => {
    setPlaybackSpeed(playbackSpeeedRate)
  }, [playbackSpeeedRate])

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height,
        display: isError ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
      <video
        ref={videoRef}
        autoPlay
        muted
        poster={thumbnail}
        playsInline
        loop={loop}
        onPlaying={() => {
          setIsError(false)
          setIsErrorOnStream?.(false)
          setRetryCount(0)
          setIsPlaying(true)
        }}
        onLoadStart={() => {
          setIsPlaying(false)
        }}
        onWaiting={() => {
          setIsPlaying(false)
        }}
        controls={showControls}
        controlsList='nodownload nofullscreen noremoteplayback'
        width='100%'
        height={height}
        style={{position: 'relative'}}
      />

      {drawBarrierCoordinates && barriers && (
        <>
          <svg width='100%' height={height} style={{position: 'absolute', top: 0, left: 0}} xmlns='http://www.w3.org/2000/svg'>
            {barriers.map((barrier, idx) => (
              <polygon
                key={idx}
                points={barrier.coordinates.map((pt) => `${pt.x * size.width},${pt.y * size.height}`).join(' ')}
                fill='none'
                stroke={colorsOnSvg[idx]}
                strokeWidth='2'
                style={{cursor: 'pointer'}}
                onMouseMove={(e) => {
                  const tooltip = tooltipRef.current
                  if (!tooltip) return
                  tooltip.textContent = `Nome da barreira: ${barrier.name}`
                  tooltip.style.left = `${e.clientX + 10}px`
                  tooltip.style.top = `${e.clientY - 10}px`
                  tooltip.style.visibility = 'visible'
                }}
                onMouseLeave={() => {
                  const tooltip = tooltipRef.current
                  if (!tooltip) return
                  tooltip.style.visibility = 'hidden'
                }}
              />
            ))}
          </svg>

          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              background: 'rgba(88, 87, 87, 0.75)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              visibility: 'hidden',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          />
        </>
      )}

      {!isPlaying && !showControls && !isError && (
        <Box position='absolute'>
          <Loading height='48px' width='48px' />
        </Box>
      )}
    </div>
  )
}

export default VideoPlayer
